import { Stat } from "views/applications-id-report/components/Stat";
import { has, toMoney } from "shared/utils";
import { Show } from "shared/components";
import { SummaryUpdated } from "../components/SummaryUpdated";
import { CriticalDataMissingWarning } from "../components/CriticalDataMissingWarning";
import {
  getDebtGrossIncomeRatio,
  getNetMonthlyIncome,
  getSelfReportedGrossMonthly,
  isIncomeVerificationSkipped,
  showValueOrPlaceholder
} from "../utils";
import { ProgressReportView } from "views/applications-id-report/components/ReportsFallback/ProgressReportView";
import { ReverifyGlobalWarning } from "./ReverifyGlobalWarning";
import {
  INCOMPLETE_APPLICATION_STATUSES,
  INCOMPLETE_VERIFICATION_STATUSES
} from "../constants";
import { ApplicationStatus } from "shared/types";
import { useCombinedIncomeContext } from "../providers/useCombinedIncome";

export function IncomeSummary() {
  const {
    applicationMeta,
    hasCreditReport,
    incomes,
    pendingReverifyRequests,
    completedReverifyRequests,
    verificationStatus,
    verificationReportData
  } = useCombinedIncomeContext();

  if (!verificationReportData) return null;

  const summary = verificationReportData.total_summary;
  const isSummaryPending =
    INCOMPLETE_VERIFICATION_STATUSES.includes(verificationStatus) &&
    INCOMPLETE_APPLICATION_STATUSES.includes(
      applicationMeta.data?.status as ApplicationStatus
    );

  const isSkipped = incomes.every(isIncomeVerificationSkipped);
  const netMonthlyIncome = getNetMonthlyIncome(verificationReportData, summary);
  const debtGrossIncomeRatio = getDebtGrossIncomeRatio(summary);

  if (isSummaryPending)
    return (
      <ProgressReportView className="max-w-xl text-center my-4">
        <p className="text-content-500 font-bold text-md mb-2">
          Income history report is in progress
        </p>
        <p className="text-content-300 font-medium text-sm">
          {incomes.length === 0
            ? "No income data yet — waiting for the applicant to begin this section"
            : "Partial data shown — waiting for the applicant to complete the application"}
        </p>
      </ProgressReportView>
    );

  return (
    <>
      <ReverifyGlobalWarning
        incomes={incomes}
        pendingReverifyRequests={pendingReverifyRequests}
        completedReverifyRequests={completedReverifyRequests}
        verificationStatus={verificationStatus}
      />
      <div className="md:p-6 pt-3 pl-4 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Stat
            title="Verified gross monthly"
            value={showValueOrPlaceholder(
              toMoney(summary.verified_gross_monthly_total),
              !isSkipped
            )}
          />
          <Stat
            title="Verified net monthly"
            value={showValueOrPlaceholder(netMonthlyIncome, !isSkipped)}
          />
          <Show if={hasCreditReport}>
            <Stat
              title="Debt to verified gross income ratio"
              value={showValueOrPlaceholder(debtGrossIncomeRatio, !isSkipped)}
            />
          </Show>

          <Stat
            title="Self-reported gross monthly"
            value={getSelfReportedGrossMonthly(verificationReportData, summary)}
          />
          <Stat
            title="Monthly assistance amount"
            value={showValueOrPlaceholder(
              toMoney(summary.monthly_housing_assistance),
              Boolean(summary.monthly_housing_assistance)
            )}
          />
          <Stat
            title="Income sources"
            value={String(summary.submitted_incomes_count)}
          />
        </div>
        <Show
          if={
            has(verificationReportData.total_overrides?.gross_monthly_income) ||
            has(verificationReportData.total_overrides?.net_monthly_income)
          }
        >
          <SummaryUpdated
            changes={verificationReportData.total_overrides}
            original={{
              gross: summary.self_reported_gross_monthly_total,
              net: summary.verified_net_monthly_total
            }}
          />
        </Show>
        <CriticalDataMissingWarning incomes={incomes} />
      </div>
    </>
  );
}
